import { AbstractControl, ValidationErrors } from '@angular/forms';
import { ProductPriceScaleDTO } from '@iota/inh-core-v1-management-client';
import { SelectableProductPriceScaleTypes } from '../constants-and-enums/product-price-scale-type';

export interface PriceScaleInterval {
    intervalBegin: number;
    intervalEnd: number;
    priceMonthly: number;
    priceYearly: number;
}

export type PriceScaleFormValue = Pick<ProductPriceScaleDTO, 'scaleType' | 'priceIntervals'>;

type IntervalBounds = Pick<PriceScaleInterval, 'intervalBegin' | 'intervalEnd'>;

export function sortIntervalsByBegin<T extends Pick<PriceScaleInterval, 'intervalBegin'>>(intervals: T[]): T[] {
    return [...(intervals ?? [])].sort((first, second) => first?.intervalBegin - second?.intervalBegin);
}

export function hasOverlappingIntervals<T extends IntervalBounds>(intervals: T[]): boolean {
    const sortedIntervals = sortIntervalsByBegin(withNumericBounds(intervals));

    let highestEnd = Number.NEGATIVE_INFINITY;
    for (const interval of sortedIntervals) {
        if (interval.intervalBegin <= highestEnd) {
            return true;
        }
        highestEnd = Math.max(highestEnd, interval.intervalEnd);
    }

    return false;
}

// Bounds are inclusive, so two intervals only connect when the next one begins right after the previous one ends.
// Overlaps are covered by their own error and must not be reported as a gap, hence the running highest end.
export function hasIntervalGaps<T extends IntervalBounds>(intervals: T[]): boolean {
    const sortedIntervals = sortIntervalsByBegin(withNumericBounds(intervals));

    if (sortedIntervals.length === 0) {
        return false;
    }

    let highestEnd = sortedIntervals[0].intervalEnd;
    for (const interval of sortedIntervals.slice(1)) {
        if (interval.intervalBegin > highestEnd + 1) {
            return true;
        }
        highestEnd = Math.max(highestEnd, interval.intervalEnd);
    }

    return false;
}

export function nextIntervalBegin<T extends Pick<PriceScaleInterval, 'intervalEnd'>>(intervals: T[]): number {
    const ends = (intervals ?? []).map((interval) => interval?.intervalEnd).filter(isNumericBound);

    if (ends.length === 0) {
        return 0;
    }

    return Math.max(...ends) + 1;
}

// Gaps are deliberately not an error here - they are surfaced as a warning.
export function priceScaleIntervalsValidator(control: AbstractControl): ValidationErrors | null {
    return hasOverlappingIntervals(control?.value as IntervalBounds[]) ? { priceIntervalsIntersect: true } : null;
}

export function checkIntervalBeginIsSmallerThanEnd(group: AbstractControl): ValidationErrors | null {
    const intervalBegin = group?.value?.intervalBegin;
    const intervalEnd = group?.value?.intervalEnd;

    if (!isNumericBound(intervalBegin) || !isNumericBound(intervalEnd)) {
        return null;
    }

    return intervalBegin >= intervalEnd ? { beginGreaterThanEnd: true } : null;
}

export function checkIsInteger(control: AbstractControl): ValidationErrors | null {
    const value = control?.value;

    return isNumericBound(value) && !Number.isInteger(value) ? { notInteger: true } : null;
}

// UNKNOWN is only ever a stored value, never a choice, and must be replaced before saving.
export function checkScaleTypeIsSelectable(control: AbstractControl): ValidationErrors | null {
    const scaleType = control?.value;

    return !!scaleType && !SelectableProductPriceScaleTypes.includes(scaleType) ? { scaleTypeNotSelectable: true } : null;
}

// A half-typed row must not report an overlap or a gap.
function withNumericBounds<T extends IntervalBounds>(intervals: T[]): T[] {
    return (intervals ?? []).filter((interval) => isNumericBound(interval?.intervalBegin) && isNumericBound(interval?.intervalEnd));
}

function isNumericBound(bound: number): boolean {
    return typeof bound === 'number' && Number.isFinite(bound);
}
